/**
 * What the red pen can draw, and how it is painted.
 *
 * Three marks, deliberately: freehand for "look here", a highlight box that
 * dims everything around it, and Hide, which paints over pixels so a reporter
 * can take a candidate's name or email out of the picture before it is
 * uploaded. Coordinates are image pixels, matching the annotator's canvases.
 */
export interface Point {
	x: number;
	y: number;
}

export type Mark =
	| { kind: "pen"; points: Point[] }
	| { kind: "highlight"; from: Point; to: Point }
	| { kind: "hide"; from: Point; to: Point };

export type MarkKind = Mark["kind"];

/** Theme colours, passed in so the canvas never hardcodes a palette. */
export interface MarkPalette {
	pen: string;
	highlight: string;
	hide: string;
	dim: string;
}

const rectOf = (from: Point, to: Point) => ({
	x: Math.min(from.x, to.x),
	y: Math.min(from.y, to.y),
	width: Math.abs(to.x - from.x),
	height: Math.abs(to.y - from.y),
});

const strokeWidthFor = (width: number): number => Math.max(4, width / 200);

const drawPen = (
	context: CanvasRenderingContext2D,
	points: Point[],
	width: number,
	color: string
): void => {
	context.strokeStyle = color;
	context.lineWidth = strokeWidthFor(width);
	context.lineCap = "round";
	context.lineJoin = "round";
	context.beginPath();
	points.forEach((point, index) => {
		if (index === 0) context.moveTo(point.x, point.y);
		else context.lineTo(point.x, point.y);
	});
	context.stroke();
};

/**
 * Paints the whole mark layer. Highlights dim everything around them with a
 * single even-odd fill, so N highlights still cost one composite.
 */
export const drawMarks = (
	context: CanvasRenderingContext2D,
	marks: readonly Mark[],
	width: number,
	height: number,
	palette: MarkPalette
): void => {
	const highlights = marks.filter((mark) => mark.kind === "highlight");

	if (highlights.length > 0) {
		const mask = new Path2D();
		mask.rect(0, 0, width, height);
		highlights.forEach((mark) => {
			const rect = rectOf(mark.from, mark.to);
			mask.rect(rect.x, rect.y, rect.width, rect.height);
		});
		context.fillStyle = palette.dim;
		context.fill(mask, "evenodd");
	}

	marks.forEach((mark) => {
		if (mark.kind === "pen") {
			drawPen(context, mark.points, width, palette.pen);
			return;
		}
		const rect = rectOf(mark.from, mark.to);
		if (mark.kind === "hide") {
			context.fillStyle = palette.hide;
			context.fillRect(rect.x, rect.y, rect.width, rect.height);
			return;
		}
		context.strokeStyle = palette.highlight;
		context.lineWidth = strokeWidthFor(width);
		context.strokeRect(rect.x, rect.y, rect.width, rect.height);
	});
};

/** A tap that never became a drag is a miss, not an empty mark. */
export const isMarkTooSmall = (mark: Mark, width: number): boolean => {
	if (mark.kind === "pen") return mark.points.length < 2;
	const rect = rectOf(mark.from, mark.to);
	const minimum = width / 200;
	return rect.width < minimum && rect.height < minimum;
};
